import React, { useEffect, useState } from "react";
import { Eye, Check, X, RefreshCcw, Search, FileText, AlertCircle } from "lucide-react";
import api from "../../lib/api";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { adminNav } from "../../components/shared/navConfig";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

const tabs = [
  { key: "Pending", label: "Pending" },
  { key: "Approved", label: "Approved" },
  { key: "Rejected", label: "Rejected" },
  { key: "Revision Required", label: "Revisions" },
];

export default function AdminTaskApproval() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("Pending");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [noteFor, setNoteFor] = useState(null); // { task, decision }
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/tasks?status=${encodeURIComponent(tab)}`); setRows(data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const act = async (task, decision) => {
    if (decision !== "approve") { setNoteFor({ task, decision }); return; }
    await review(task.task_id, "approve", null);
  };

  const review = async (task_id, decision, n) => {
    try {
      await api.post(`/admin/tasks/${task_id}/review`, { decision, note: n });
      toast.success(`${decision === "approve" ? "Approved" : decision === "reject" ? "Rejected" : "Changes requested"} · ${task_id}`);
      setNoteFor(null); setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const filtered = rows.filter(r => !q || (r.title || "").toLowerCase().includes(q.toLowerCase()) || (r.company_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <DashboardShell nav={adminNav} title="Task approval queue" subtitle="Review, approve or request changes on company-submitted tasks">
      <div className="card-surface p-5">
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-2 rounded-full border flex-1 max-w-md" style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
            <Search size={13} className="text-zinc-500"/>
            <input data-testid="task-approval-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search tasks or companies…" className="bg-transparent outline-none text-sm text-white placeholder:text-zinc-600 flex-1"/>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-full border" style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
            {tabs.map(t => (
              <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors duration-200 ${tab === t.key ? "bg-blue-500/15 text-blue-300" : "text-zinc-400 hover:text-white"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-zinc-500">
            <AlertCircle size={22} className="mx-auto mb-3 text-zinc-600"/>
            No tasks in “{tab}” queue.
          </div>
        ) : (
          <table className="table-stack w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 text-left border-b" style={{ borderColor: "var(--border)" }}>
                <th className="pb-3 font-normal">Task</th>
                <th className="pb-3 font-normal">Company</th>
                <th className="pb-3 font-normal">Location</th>
                <th className="pb-3 font-normal">Budget</th>
                <th className="pb-3 font-normal">Deadline</th>
                <th className="pb-3 font-normal">Status</th>
                <th className="pb-3 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.task_id} data-testid={`approval-row-${t.task_id}`} className="border-b last:border-b-0" style={{ borderColor: "var(--border)" }}>
                  <td className="py-4">
                    <div className="text-white">{t.title}</div>
                    <div className="text-[11px] text-zinc-500 font-mono">{t.task_id}</div>
                  </td>
                  <td data-label="Company" className="text-zinc-300 text-sm"><span>{t.company_name}</span></td>
                  <td data-label="Location" className="text-zinc-400 text-xs"><span>{t.city || t.work_mode}</span></td>
                  <td data-label="Budget" className="text-white font-mono text-xs"><span>₹{Number(t.budget || 0).toLocaleString()}</span></td>
                  <td data-label="Deadline" className="text-zinc-400 text-xs"><span>{t.deadline || "—"}</span></td>
                  <td data-label="Status"><StatusBadge status={t.status}/></td>
                  <td className="stack-full">
                    <div className="flex items-center gap-1.5 justify-end">
                      <IconBtn data-testid={`view-btn-${t.task_id}`} title="View" icon={Eye} onClick={() => setViewing(t)}/>
                      <IconBtn data-testid={`approve-btn-${t.task_id}`} title="Approve" icon={Check} tone="ok"    onClick={() => act(t, "approve")} disabled={t.status === "Approved"}/>
                      <IconBtn data-testid={`revise-btn-${t.task_id}`}  title="Request changes" icon={RefreshCcw} tone="warn" onClick={() => act(t, "request_changes")} disabled={t.status === "Approved"}/>
                      <IconBtn data-testid={`reject-btn-${t.task_id}`}  title="Reject" icon={X} tone="bad"    onClick={() => act(t, "reject")}/>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* View task modal */}
      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="max-w-2xl" style={{ background: "var(--surface)", borderColor: "var(--border-strong)", color: "#F8F9FA" }}>
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{viewing.title}</DialogTitle>
                <DialogDescription className="text-zinc-500">
                  {viewing.company_name} · <span className="font-mono">{viewing.task_id}</span> · <StatusBadge status={viewing.status}/>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <KV k="Description" v={viewing.description}/>
                <div className="grid grid-cols-2 gap-3">
                  <KV k="Category"     v={viewing.category}/>
                  <KV k="Budget"       v={`₹${Number(viewing.budget || 0).toLocaleString()}`}/>
                  <KV k="Education"    v={viewing.education || "—"}/>
                  <KV k="Experience"   v={viewing.experience || "—"}/>
                  <KV k="Work mode"    v={viewing.work_mode || "—"}/>
                  <KV k="Working hours" v={viewing.working_hours || "—"}/>
                  <KV k="Start"        v={`${viewing.start_date || "—"}${viewing.start_time ? " · " + viewing.start_time : ""}`}/>
                  <KV k="Deadline"     v={viewing.deadline || "—"}/>
                  <KV k="City"         v={viewing.city || "—"}/>
                  <KV k="Address"      v={viewing.address || "—"}/>
                </div>
                <KV k="Skills" v={(viewing.skills || []).join(", ") || "—"}/>
                <KV k="Attachments" v={(viewing.attachments || []).length ? (viewing.attachments || []).map(f => <a key={f} className="text-blue-400 hover:text-blue-300 mr-2" href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f}/download`} target="_blank" rel="noreferrer"><FileText size={11} className="inline"/> file</a>) : "None"}/>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Note modal */}
      <Dialog open={!!noteFor} onOpenChange={() => setNoteFor(null)}>
        <DialogContent className="max-w-md" style={{ background: "var(--surface)", borderColor: "var(--border-strong)", color: "#F8F9FA" }}>
          {noteFor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {noteFor.decision === "reject" ? "Reject task" : "Request changes"}
                </DialogTitle>
                <DialogDescription className="text-zinc-500">
                  {noteFor.task.title} — the note is emailed to the company.
                </DialogDescription>
              </DialogHeader>
              <textarea data-testid="admin-review-note" value={note} onChange={e => setNote(e.target.value)} rows={4}
                        placeholder="Explain what's missing or incorrect…"
                        className="w-full px-3 py-2 rounded-md border bg-[#111114] text-sm text-white placeholder:text-zinc-600 outline-none resize-none focus:border-blue-500/50"
                        style={{ borderColor: "var(--border-strong)" }}/>
              <DialogFooter>
                <button onClick={() => { setNoteFor(null); setNote(""); }} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
                <button data-testid="admin-review-confirm" onClick={() => review(noteFor.task.task_id, noteFor.decision, note || null)} className="btn-primary px-4 py-2 rounded-md text-sm">Send</button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

const IconBtn = ({ icon: Icon, tone, disabled, ...rest }) => {
  const colors = { ok: "text-emerald-400 hover:bg-emerald-500/10", warn: "text-amber-400 hover:bg-amber-500/10", bad: "text-rose-400 hover:bg-rose-500/10" }[tone] || "text-zinc-300 hover:bg-white/5";
  return <button {...rest} disabled={disabled} className={`w-8 h-8 rounded-md border flex items-center justify-center transition-colors duration-200 disabled:opacity-30 ${colors}`} style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
    <Icon size={13}/>
  </button>;
};

const KV = ({ k, v }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{k}</div>
    <div className="text-sm text-zinc-200 mt-0.5">{v}</div>
  </div>
);
